/**
 * Capa de Datos (DL) para la dinámica "Xolo Fest 2026 — Pasaporte Digital, Zonas y Tómbola Perrona" (Tile 53).
 * Conexión directa con la API RESTful de .NET 8 (/api/v1/xf).
 */

const rawApiUrl = process.env.REACT_APP_API_URL || '/api/';
const API_URL = rawApiUrl.endsWith('/') ? rawApiUrl : `${rawApiUrl}/`;

const RATE_LIMIT_DEFAULT_MESSAGE =
    'Has excedido el límite de intentos permitidos. Por seguridad, espera 1 minuto antes de volver a intentar.';

/**
 * Construye los encabezados HTTP estándar incluyendo el identificador de sesión defensivo de Xolo Fest.
 * @param {string|null} sessionId - UUID de sesión del navegador
 * @returns {Record<string, string>}
 */
const buildHeaders = (sessionId = null) => {
    const headers = {
        'Content-Type': 'application/json'
    };
    if (sessionId) {
        headers['X-Xf-Session-Id'] = sessionId;
    }
    return headers;
};

/**
 * Obtiene la configuración pública activa del evento Xolo Fest.
 * GET /api/v1/xf/public/config
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export const GetXfPublicConfig = async () => {
    try {
        const response = await fetch(`${API_URL}v1/xf/public/config`, {
            method: 'GET',
            headers: buildHeaders()
        });

        const resData = await response.json().catch(() => ({}));

        if (response.status === 429) {
            return {
                success: false,
                isRateLimited: true,
                status: 429,
                message: resData.message || RATE_LIMIT_DEFAULT_MESSAGE
            };
        }

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: resData.message || 'No fue posible cargar la configuración pública de Xolo Fest.'
            };
        }

        const configPayload = resData.data || resData.config || resData;
        return {
            success: true,
            data: configPayload,
            ...resData
        };
    } catch (error) {
        console.error('Error en GetXfPublicConfig DL:', error);
        return {
            success: false,
            message: 'Error de conexión con el servidor al consultar la configuración del evento.'
        };
    }
};

/**
 * Valida criptográficamente el token compacto (?t=...) escaneado desde el QR físico del boleto.
 * GET /api/v1/xf/public/validate-ticket?t={token}
 * @param {string} token - Token compacto Base64Url ({folioBase64Url}.{hmac16Base64Url})
 * @param {string|null} sessionId - Identificador de sesión para control de rate-limit
 * @returns {Promise<object>} Resultado con isValid, ticketFolio, category, allowedZones, isRegistered, message
 */
export const ValidateXfTicketToken = async (token, sessionId = null) => {
    try {
        const response = await fetch(
            `${API_URL}v1/xf/public/validate-ticket?t=${encodeURIComponent(token || '')}`,
            {
                method: 'GET',
                headers: buildHeaders(sessionId)
            }
        );

        const resData = await response.json().catch(() => ({}));

        if (response.status === 429) {
            return {
                success: false,
                isValid: false,
                isRateLimited: true,
                status: 429,
                message: resData.message || resData.errorMessage || RATE_LIMIT_DEFAULT_MESSAGE
            };
        }

        if (!response.ok) {
            return {
                success: false,
                isValid: false,
                status: response.status,
                message:
                    resData.message ||
                    resData.errorMessage ||
                    'El código QR escaneado es inválido o su firma de seguridad fue alterada.',
                ...resData
            };
        }

        return {
            success: Boolean(resData.isValid ?? resData.success ?? true),
            ...resData
        };
    } catch (error) {
        console.error('Error en ValidateXfTicketToken DL:', error);
        return {
            success: false,
            isValid: false,
            message: 'Error de conexión con el servidor al verificar la autenticidad del código QR.'
        };
    }
};

/**
 * Registra en 1 solo paso el boleto de un aficionado en el padrón oficial y le asigna folio para la Tómbola Perrona.
 * Si el mismo aficionado reingresa sus mismos datos, auto-recupera su sesión de forma idempotente.
 * POST /api/v1/xf/public/register
 * @param {object} payload - { ticketFolio, fullName, phone, email, verificationToken }
 * @param {string|null} sessionId - Identificador de sesión para rate-limit defensivo
 * @returns {Promise<object>} Respuesta del servidor con participant, participantToken, isAutoRecovered, etc.
 */
export const RegisterXfParticipant = async (payload, sessionId = null) => {
    try {
        const cleanPhone = String(payload?.phone || '').replace(/\D/g, '');
        const normalizedBody = {
            ...payload,
            ticketFolio: (payload?.ticketFolio || '').trim().toUpperCase(),
            fullName: (payload?.fullName || '').trim(),
            phone: cleanPhone,
            email: (payload?.email || '').trim().toLowerCase(),
            verificationToken: payload?.verificationToken || payload?.token || null,
            token: payload?.token || payload?.verificationToken || null
        };

        const response = await fetch(`${API_URL}v1/xf/public/register`, {
            method: 'POST',
            headers: buildHeaders(sessionId),
            body: JSON.stringify(normalizedBody)
        });

        const resData = await response.json().catch(() => ({}));

        if (response.status === 429) {
            return {
                success: false,
                isRateLimited: true,
                status: 429,
                message: resData.message || RATE_LIMIT_DEFAULT_MESSAGE,
                ...resData
            };
        }

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message:
                    resData.message ||
                    'No fue posible procesar el registro con los datos ingresados. Verifica tu folio e intenta de nuevo.',
                ...resData
            };
        }

        return {
            success: resData.success !== undefined ? resData.success : true,
            ...resData
        };
    } catch (error) {
        console.error('Error en RegisterXfParticipant DL:', error);
        return {
            success: false,
            message: 'Error de conexión con el servidor al registrar tu boleto.'
        };
    }
};

/**
 * Recupera el pase digital e inscripción a la Tómbola Perrona validando Folio + Teléfono o Correo Electrónico.
 * POST /api/v1/xf/public/recover
 * @param {object} payload - { ticketFolio, contactQuery / emailOrPhone / phone / email }
 * @param {string|null} sessionId - Identificador de sesión para rate-limit defensivo
 * @returns {Promise<object>} Respuesta del servidor con participant y participantToken
 */
export const RecoverXfPass = async (payload, sessionId = null) => {
    try {
        const rawContact = (
            payload?.contactQuery ||
            payload?.emailOrPhone ||
            payload?.contact ||
            payload?.query ||
            payload?.email ||
            payload?.phone ||
            ''
        ).trim();

        const normalizedBody = {
            ...payload,
            ticketFolio: (payload?.ticketFolio || '').trim().toUpperCase(),
            contactQuery: rawContact,
            emailOrPhone: rawContact,
            contact: rawContact,
            query: rawContact
        };

        const response = await fetch(`${API_URL}v1/xf/public/recover`, {
            method: 'POST',
            headers: buildHeaders(sessionId),
            body: JSON.stringify(normalizedBody)
        });

        const resData = await response.json().catch(() => ({}));

        if (response.status === 429) {
            return {
                success: false,
                isRateLimited: true,
                status: 429,
                message: resData.message || RATE_LIMIT_DEFAULT_MESSAGE,
                ...resData
            };
        }

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message:
                    resData.message ||
                    'No encontramos un registro coincidente con ese Folio y Teléfono/Correo.',
                ...resData
            };
        }

        return {
            success: resData.success !== undefined ? resData.success : true,
            ...resData
        };
    } catch (error) {
        console.error('Error en RecoverXfPass DL:', error);
        return {
            success: false,
            message: 'Error de conexión con el servidor al intentar recuperar tu pase.'
        };
    }
};

/**
 * Obtiene una muestra de boletos disponibles en lista blanca (GENERAL, VIP, CANCHA) para pruebas en Modo Demo.
 * GET /api/v1/xf/public/demo-tickets
 * @returns {Promise<{ success: boolean, tickets: Array, data?: any, message?: string }>}
 */
export const GetXfDemoSampleTickets = async () => {
    try {
        const response = await fetch(`${API_URL}v1/xf/public/demo-tickets`, {
            method: 'GET',
            headers: buildHeaders()
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                tickets: [],
                message: resData.message || 'No fue posible obtener los boletos de demostración.'
            };
        }

        const list = Array.isArray(resData)
            ? resData
            : Array.isArray(resData.tickets)
            ? resData.tickets
            : Array.isArray(resData.items)
            ? resData.items
            : Array.isArray(resData.data)
            ? resData.data
            : [];

        return {
            success: true,
            tickets: list,
            data: resData
        };
    } catch (error) {
        console.error('Error en GetXfDemoSampleTickets DL:', error);
        return {
            success: false,
            tickets: [],
            message: 'Error de conexión al cargar boletos de prueba.'
        };
    }
};

/**
 * Consulta el estado completo del Pasaporte Digital y de la Tómbola Perrona por folio de boleto.
 * GET /api/v1/xf/public/passport/{ticketFolio}?t={token}
 * @param {string} ticketFolio - Folio del boleto (ej. XOLO-0001)
 * @param {string|null} token - Token opcional de verificación o sesión
 * @returns {Promise<object>} Estado del participante, progreso de sellos y elegibilidad de tómbola
 */
export const GetXfPassportByFolio = async (ticketFolio, token = null) => {
    try {
        const cleanFolio = (ticketFolio || '').trim().toUpperCase();
        const querySuffix = token ? `?t=${encodeURIComponent(token)}` : '';
        const response = await fetch(
            `${API_URL}v1/xf/public/passport/${encodeURIComponent(cleanFolio)}${querySuffix}`,
            {
                method: 'GET',
                headers: buildHeaders()
            }
        );

        const resData = await response.json().catch(() => ({}));

        if (response.status === 429) {
            return {
                success: false,
                isRateLimited: true,
                status: 429,
                message: resData.message || RATE_LIMIT_DEFAULT_MESSAGE
            };
        }

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: resData.message || 'No se encontró información del pasaporte para el folio indicado.',
                ...resData
            };
        }

        return {
            success: resData.success !== undefined ? resData.success : true,
            ...resData
        };
    } catch (error) {
        console.error('Error en GetXfPassportByFolio DL:', error);
        return {
            success: false,
            message: 'Error de conexión al consultar el Pasaporte Digital.'
        };
    }
};

// ============================================================================
// MÉTODOS DE ADMINISTRACIÓN Y SIMULADOR DEMO DE PASAPORTE (HU-03)
// ============================================================================

/**
 * Actualiza en vivo la configuración global de Xolo Fest (PassportDisplayMode, StampUnlockRule, etc.)
 * y emite broadcast SignalR (XfConfigUpdated).
 * PUT /api/v1/xf/admin/config
 * @param {object} configPayload - Campos a actualizar
 * @returns {Promise<object>}
 */
export const UpdateXfAdminConfig = async (configPayload) => {
    try {
        const response = await fetch(`${API_URL}v1/xf/admin/config`, {
            method: 'PUT',
            headers: buildHeaders(),
            body: JSON.stringify(configPayload || {})
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: resData.message || 'No fue posible actualizar la configuración de Xolo Fest.',
                ...resData
            };
        }

        return {
            success: resData.success !== undefined ? resData.success : true,
            data: resData.data || resData.config || resData,
            ...resData
        };
    } catch (error) {
        console.error('Error en UpdateXfAdminConfig DL:', error);
        return {
            success: false,
            message: 'Error de conexión al actualizar la configuración.'
        };
    }
};

/**
 * Simula el escaneo/estampado de un sello en cualquier estación (P1_ACCESO, POS_XOLOSHOP, POS_TACOS, ACT_CALIENTE, ACT_NISSAN)
 * evaluando en tiempo real el motor de recompensas y emitiendo eventos SignalR.
 * POST /api/v1/xf/admin/demo/simulate-stamp
 * @param {object} payload - { ticketFolio, stationCode, customActionName, amount, activationResult }
 * @returns {Promise<object>} Vista actualizada del pasaporte (XfPublicPassportViewDto)
 */
export const SimulateXfStamp = async (payload) => {
    try {
        const body = {
            ticketFolio: (payload?.ticketFolio || '').trim().toUpperCase(),
            stationCode: payload?.stationCode || 'P1_ACCESO',
            customActionName: payload?.customActionName || null,
            amount: payload?.amount ?? null,
            activationResult: payload?.activationResult || null
        };

        const response = await fetch(`${API_URL}v1/xf/admin/demo/simulate-stamp`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify(body)
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: resData.message || 'No fue posible simular el sello en la estación seleccionada.',
                ...resData
            };
        }

        return {
            success: resData.success !== undefined ? resData.success : true,
            ...resData
        };
    } catch (error) {
        console.error('Error en SimulateXfStamp DL:', error);
        return {
            success: false,
            message: 'Error de conexión al simular el sello en el pasaporte.'
        };
    }
};

/**
 * Simula el nivel de inventario físico de souvenirs para probar Cupón Dorado vs Fallback Insignia Digital.
 * POST /api/v1/xf/admin/demo/simulate-stock
 * @param {string|object} modeOrPayload - 'AVAILABLE' | 'LAST_ONE' | 'EXHAUSTED' o { mode }
 * @returns {Promise<object>} Configuración actualizada
 */
export const SimulateXfStock = async (modeOrPayload) => {
    try {
        const mode =
            typeof modeOrPayload === 'string'
                ? modeOrPayload
                : modeOrPayload?.mode || 'AVAILABLE';

        const response = await fetch(`${API_URL}v1/xf/admin/demo/simulate-stock`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({ mode })
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: resData.message || 'No fue posible simular el cambio de stock.',
                ...resData
            };
        }

        return {
            success: resData.success !== undefined ? resData.success : true,
            data: resData.data || resData.config || resData,
            ...resData
        };
    } catch (error) {
        console.error('Error en SimulateXfStock DL:', error);
        return {
            success: false,
            message: 'Error de conexión al simular el stock de premios.'
        };
    }
};

/**
 * Reinicia el recorrido del participante en Modo Demo (HasAttended = false, limpia sellos y recompensas).
 * POST /api/v1/xf/admin/demo/reset-participant
 * @param {string} ticketFolio - Folio del boleto a reiniciar
 * @returns {Promise<object>} Vista reiniciada del pasaporte
 */
export const ResetXfParticipantDemo = async (ticketFolio) => {
    try {
        const cleanFolio = (
            typeof ticketFolio === 'string'
                ? ticketFolio
                : ticketFolio?.ticketFolio || ''
        )
            .trim()
            .toUpperCase();

        const response = await fetch(`${API_URL}v1/xf/admin/demo/reset-participant`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({ ticketFolio: cleanFolio })
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: resData.message || 'No fue posible reiniciar el recorrido del participante.',
                ...resData
            };
        }

        return {
            success: resData.success !== undefined ? resData.success : true,
            ...resData
        };
    } catch (error) {
        console.error('Error en ResetXfParticipantDemo DL:', error);
        return {
            success: false,
            message: 'Error de conexión al reiniciar la demostración del participante.'
        };
    }
};

// ============================================================================
// MÉTODOS DEL SCANNER WEB PWA MULTI-ESTACIÓN (HU-04: CONTROL DE ACCESOS Y ZONAS)
// ============================================================================

/**
 * Construye encabezados HTTP con Bearer Token para el operador del Scanner PWA.
 * @param {string|null} operatorJwt - Token JWT del operador autenticado
 * @returns {Record<string, string>}
 */
const buildOperatorHeaders = (operatorJwt = null) => {
    const headers = {
        'Content-Type': 'application/json'
    };
    if (operatorJwt) {
        headers.Authorization = `Bearer ${operatorJwt}`;
    }
    return headers;
};

/**
 * Autentica un operador en Modo Demo en 1 clic obteniendo un JWT válido con rol Operador.
 * POST /api/v1/xf/scanner/demo-auth
 * @param {string} username - Usuario demo (ej. 'staff_puerta1', 'staff_vip', 'staff_cancha')
 * @param {string} role - Rol solicitado (por defecto 'Operador')
 * @returns {Promise<object>} { success, token, username, role, expiresInMinutes, message }
 */
export const AuthenticateXfDemoOperator = async (username = 'staff_puerta1', role = 'Operador') => {
    try {
        const response = await fetch(`${API_URL}v1/xf/scanner/demo-auth`, {
            method: 'POST',
            headers: buildOperatorHeaders(),
            body: JSON.stringify({
                username: (username || 'staff_puerta1').trim(),
                role: (role || 'Operador').trim()
            })
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message:
                    resData.message ||
                    'No fue posible iniciar sesión con el operador demo seleccionado.',
                ...resData
            };
        }

        return {
            success: resData.success !== undefined ? resData.success : true,
            token: resData.token || resData.accessToken || '',
            username: resData.username || username,
            role: resData.role || role,
            expiresInMinutes: resData.expiresInMinutes || 60,
            ...resData
        };
    } catch (error) {
        console.error('Error en AuthenticateXfDemoOperator DL:', error);
        return {
            success: false,
            message: 'Error de conexión al autenticar operador demo.'
        };
    }
};

/**
 * Autentica un operador real del sistema Interaktia V2 mediante credenciales estándar.
 * POST /api/v2/auth/login
 * @param {string} username - Nombre de usuario o correo del operador
 * @param {string} password - Contraseña del operador
 * @returns {Promise<object>} { success, token, username, role, message }
 */
export const LoginXfRealOperator = async (username, password) => {
    try {
        const response = await fetch(`${API_URL}v2/auth/login`, {
            method: 'POST',
            headers: buildOperatorHeaders(),
            body: JSON.stringify({
                username: (username || '').trim(),
                password: password || ''
            })
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message:
                    resData.message ||
                    resData.title ||
                    'Credenciales de operador inválidas o sin permisos.',
                ...resData
            };
        }

        const token =
            resData.token ||
            resData.accessToken ||
            resData.jwt ||
            resData.data?.token ||
            resData.data?.accessToken ||
            '';

        return {
            success: Boolean(token),
            token,
            username: resData.username || resData.user?.username || (username || '').trim(),
            role: resData.role || resData.user?.role || 'Operador',
            expiresInMinutes: resData.expiresInMinutes || 120,
            ...resData
        };
    } catch (error) {
        console.error('Error en LoginXfRealOperator DL:', error);
        return {
            success: false,
            message: 'Error de conexión con el servidor de autenticación.'
        };
    }
};

/**
 * Obtiene el catálogo de estaciones operativas activas (puertas, tiendas POS, activaciones) con su zona y aforo.
 * GET /api/v1/xf/scanner/stations
 * @param {string} operatorJwt - Token JWT del operador
 * @param {string|null} stationType - Filtro opcional ('ACCESS', 'POS_STORE', 'ACTIVATION')
 * @returns {Promise<{ success: boolean, stations: Array, isUnauthorized?: boolean, message?: string }>}
 */
export const GetXfScannerStations = async (operatorJwt, stationType = null) => {
    try {
        const query = stationType ? `?stationType=${encodeURIComponent(stationType)}` : '';
        const response = await fetch(`${API_URL}v1/xf/scanner/stations${query}`, {
            method: 'GET',
            headers: buildOperatorHeaders(operatorJwt)
        });

        if (response.status === 401 || response.status === 403) {
            return {
                success: false,
                isUnauthorized: true,
                status: response.status,
                stations: [],
                message: 'Sesión de operador expirada o no autorizada.'
            };
        }

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                stations: [],
                message: resData.message || 'No fue posible consultar las estaciones del Scanner.'
            };
        }

        const stations = Array.isArray(resData)
            ? resData
            : Array.isArray(resData.stations)
            ? resData.stations
            : Array.isArray(resData.data)
            ? resData.data
            : [];

        return {
            success: true,
            stations,
            data: resData
        };
    } catch (error) {
        console.error('Error en GetXfScannerStations DL:', error);
        return {
            success: false,
            stations: [],
            message: 'Error de conexión al cargar las estaciones operativas.'
        };
    }
};

/**
 * Obtiene el estado de aforo en vivo y semáforo de Protección Civil de todas las zonas del evento.
 * GET /api/v1/xf/scanner/zones
 * @param {string} operatorJwt - Token JWT del operador
 * @returns {Promise<{ success: boolean, zones: Array, isUnauthorized?: boolean, message?: string }>}
 */
export const GetXfScannerZones = async (operatorJwt) => {
    try {
        const response = await fetch(`${API_URL}v1/xf/scanner/zones`, {
            method: 'GET',
            headers: buildOperatorHeaders(operatorJwt)
        });

        if (response.status === 401 || response.status === 403) {
            return {
                success: false,
                isUnauthorized: true,
                status: response.status,
                zones: [],
                message: 'Sesión de operador expirada o no autorizada.'
            };
        }

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                zones: [],
                message: resData.message || 'No fue posible consultar el aforo de zonas.'
            };
        }

        const zones = Array.isArray(resData)
            ? resData
            : Array.isArray(resData.zones)
            ? resData.zones
            : Array.isArray(resData.data)
            ? resData.data
            : [];

        return {
            success: true,
            zones,
            data: resData
        };
    } catch (error) {
        console.error('Error en GetXfScannerZones DL:', error);
        return {
            success: false,
            zones: [],
            message: 'Error de conexión al consultar el aforo de Protección Civil.'
        };
    }
};

/**
 * Valida de forma atómica el acceso de un boleto o token QR en una puerta o zona restringida (HU-04).
 * POST /api/v1/xf/scanner/check-access
 * @param {object} payload - { ticketFolioOrToken, stationCode, scanMethod }
 * @param {string} operatorJwt - Token JWT del operador
 * @returns {Promise<object>} XfCheckAccessResultDto con status, colorTheme, audioCue, title, message, etc.
 */
export const CheckXfAccessScan = async (payload, operatorJwt) => {
    try {
        const body = {
            ticketFolioOrToken: String(
                payload?.ticketFolioOrToken || payload?.ticketFolio || payload?.token || ''
            ).trim(),
            stationCode: String(payload?.stationCode || 'P1_ACCESO').trim(),
            scanMethod: String(payload?.scanMethod || 'CAMERA').trim().toUpperCase(),
            clientScanId: payload?.clientScanId || null
        };

        const response = await fetch(`${API_URL}v1/xf/scanner/check-access`, {
            method: 'POST',
            headers: buildOperatorHeaders(operatorJwt),
            body: JSON.stringify(body)
        });

        if (response.status === 401 || response.status === 403) {
            return {
                success: false,
                isUnauthorized: true,
                status: response.status,
                message: 'Sesión de operador expirada o no autorizada.'
            };
        }

        const resData = await response.json().catch(() => ({}));

        if (!response.ok && !resData.status) {
            return {
                success: false,
                status: 'INVALID_TICKET',
                colorTheme: 'RED',
                audioCue: 'ERROR_BUZZER',
                title: 'ERROR DE VALIDACIÓN',
                message: resData.message || 'No fue posible validar el acceso del boleto.',
                ...resData
            };
        }

        return {
            success: Boolean(resData.success),
            status: resData.status || (resData.success ? 'GRANTED' : 'INVALID_TICKET'),
            colorTheme: resData.colorTheme || (resData.success ? 'GREEN' : 'RED'),
            audioCue: resData.audioCue || (resData.success ? 'SUCCESS_ARPEGGIO' : 'ERROR_BUZZER'),
            ...resData
        };
    } catch (error) {
        console.error('Error en CheckXfAccessScan DL:', error);
        return {
            success: false,
            status: 'NETWORK_ERROR',
            colorTheme: 'RED',
            audioCue: 'ERROR_BUZZER',
            title: 'ERROR DE CONEXIÓN',
            message: 'No fue posible conectar con el servidor de control de accesos.'
        };
    }
};

/**
 * Obtiene la bitácora en vivo de los últimos escaneos de acceso registrados en puerta/zona.
 * GET /api/v1/xf/scanner/recent-logs
 * @param {string} operatorJwt - Token JWT del operador
 * @param {string|null} stationCode - Filtro opcional por código de estación
 * @param {number} limit - Cantidad máxima de registros (por defecto 20)
 * @returns {Promise<{ success: boolean, logs: Array, isUnauthorized?: boolean, message?: string }>}
 */
export const GetXfRecentAccessLogs = async (operatorJwt, stationCode = null, limit = 20) => {
    try {
        const params = new URLSearchParams();
        if (stationCode) {
            params.set('stationCode', stationCode);
        }
        if (limit) {
            params.set('limit', String(limit));
        }
        const queryString = params.toString() ? `?${params.toString()}` : '';

        const response = await fetch(`${API_URL}v1/xf/scanner/recent-logs${queryString}`, {
            method: 'GET',
            headers: buildOperatorHeaders(operatorJwt)
        });

        if (response.status === 401 || response.status === 403) {
            return {
                success: false,
                isUnauthorized: true,
                status: response.status,
                logs: [],
                message: 'Sesión de operador expirada o no autorizada.'
            };
        }

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                logs: [],
                message: resData.message || 'No fue posible consultar la bitácora de accesos.'
            };
        }

        const logs = Array.isArray(resData)
            ? resData
            : Array.isArray(resData.logs)
            ? resData.logs
            : Array.isArray(resData.items)
            ? resData.items
            : Array.isArray(resData.data)
            ? resData.data
            : [];

        return {
            success: true,
            logs,
            data: resData
        };
    } catch (error) {
        console.error('Error en GetXfRecentAccessLogs DL:', error);
        return {
            success: false,
            logs: [],
            message: 'Error de conexión al consultar la bitácora reciente de estación.'
        };
    }
};

// ============================================================================
// MÉTODOS DEL SCANNER WEB PWA: POS TÁCTIL, ACTIVACIONES Y CANJE (HU-05)
// ============================================================================

/**
 * Obtiene el catálogo de productos rápidos para una estación POS_STORE (ej. STAND_XOLOSHOP, STAND_TACOS).
 * GET /api/v1/xf/scanner/stations/{stationCode}/products
 * @param {string} stationCode - Código de la estación POS
 * @param {string} operatorJwt - Token JWT del operador
 * @returns {Promise<{ success: boolean, products: Array, isUnauthorized?: boolean, message?: string }>}
 */
export const GetXfStationProducts = async (stationCode, operatorJwt) => {
    try {
        const cleanStation = String(stationCode || 'STAND_XOLOSHOP').trim();
        const response = await fetch(
            `${API_URL}v1/xf/scanner/stations/${encodeURIComponent(cleanStation)}/products`,
            {
                method: 'GET',
                headers: buildOperatorHeaders(operatorJwt)
            }
        );

        if (response.status === 401 || response.status === 403) {
            return {
                success: false,
                isUnauthorized: true,
                status: response.status,
                products: [],
                message: 'Sesión de operador expirada o no autorizada.'
            };
        }

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                products: [],
                message: resData.message || 'No fue posible cargar el catálogo POS de la estación.'
            };
        }

        const products = Array.isArray(resData)
            ? resData
            : Array.isArray(resData.products)
            ? resData.products
            : Array.isArray(resData.items)
            ? resData.items
            : Array.isArray(resData.data)
            ? resData.data
            : [];

        return {
            success: true,
            products,
            data: resData
        };
    } catch (error) {
        console.error('Error en GetXfStationProducts DL:', error);
        return {
            success: false,
            products: [],
            message: 'Error de conexión al cargar productos del Punto de Venta.'
        };
    }
};

/**
 * Identifica rápidamente a un participante por folio, token QR o código de cupón (Flujo Dual: Escaneo Primero -> Operar Después).
 * GET /api/v1/xf/scanner/lookup-participant?q={query}
 * @param {string} query - Folio, token QR o código #CANJE
 * @param {string} operatorJwt - Token JWT del operador
 * @returns {Promise<object>} XfLookupParticipantScannerDto
 */
export const LookupXfParticipantForScanner = async (query, operatorJwt) => {
    try {
        const cleanQuery = String(query || '').trim();
        const response = await fetch(
            `${API_URL}v1/xf/scanner/lookup-participant?q=${encodeURIComponent(cleanQuery)}`,
            {
                method: 'GET',
                headers: buildOperatorHeaders(operatorJwt)
            }
        );

        if (response.status === 401 || response.status === 403) {
            return {
                success: false,
                isUnauthorized: true,
                status: response.status,
                message: 'Sesión de operador expirada o no autorizada.'
            };
        }

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: resData.message || 'No se encontró un boleto o participante válido.',
                ...resData
            };
        }

        return {
            success: Boolean(resData.success ?? true),
            ...resData
        };
    } catch (error) {
        console.error('Error en LookupXfParticipantForScanner DL:', error);
        return {
            success: false,
            message: 'Error de conexión al identificar al aficionado.'
        };
    }
};

/**
 * Registra una compra y gasto real en una estación POS_STORE y estampa el sello correspondiente en el Pasaporte Digital (HU-05).
 * POST /api/v1/xf/scanner/record-purchase
 * @param {object} payload - { ticketFolioOrToken, stationCode, items, customAmount, customConcept, scanMethod }
 * @param {string} operatorJwt - Token JWT del operador
 * @returns {Promise<object>} XfRecordPurchaseResultDto
 */
export const RecordXfPosPurchase = async (payload, operatorJwt) => {
    try {
        const body = {
            ticketFolioOrToken: String(
                payload?.ticketFolioOrToken || payload?.ticketFolio || payload?.token || ''
            ).trim(),
            stationCode: String(payload?.stationCode || 'STAND_XOLOSHOP').trim(),
            items: Array.isArray(payload?.items) ? payload.items : [],
            customAmount:
                payload?.customAmount !== undefined &&
                payload?.customAmount !== null &&
                payload?.customAmount !== ''
                    ? Number(payload.customAmount)
                    : null,
            customConcept: payload?.customConcept ? String(payload.customConcept).trim() : null,
            scanMethod: String(payload?.scanMethod || 'MANUAL').trim().toUpperCase()
        };

        const response = await fetch(`${API_URL}v1/xf/scanner/record-purchase`, {
            method: 'POST',
            headers: buildOperatorHeaders(operatorJwt),
            body: JSON.stringify(body)
        });

        if (response.status === 401 || response.status === 403) {
            return {
                success: false,
                isUnauthorized: true,
                status: response.status,
                message: 'Sesión de operador expirada o no autorizada.'
            };
        }

        const resData = await response.json().catch(() => ({}));

        if (!response.ok && !resData.status) {
            return {
                success: false,
                status: 'INVALID_TICKET',
                colorTheme: 'RED',
                audioCue: 'ERROR_BUZZER',
                title: 'ERROR EN PUNTO DE VENTA',
                message: resData.message || 'No fue posible registrar el consumo en el POS.',
                ...resData
            };
        }

        return {
            success: Boolean(resData.success),
            status: resData.status || (resData.success ? 'PURCHASE_RECORDED' : 'INVALID_TICKET'),
            colorTheme: resData.colorTheme || (resData.success ? 'GREEN' : 'RED'),
            audioCue: resData.audioCue || (resData.success ? 'SUCCESS_ARPEGGIO' : 'ERROR_BUZZER'),
            ...resData
        };
    } catch (error) {
        console.error('Error en RecordXfPosPurchase DL:', error);
        return {
            success: false,
            status: 'NETWORK_ERROR',
            colorTheme: 'RED',
            audioCue: 'ERROR_BUZZER',
            title: 'ERROR DE CONEXIÓN POS',
            message: 'No fue posible conectar con el servidor para registrar la compra.'
        };
    }
};

/**
 * Registra la participación de un aficionado en una activación de patrocinador (Caliente.mx / Nissan)
 * y evalúa la regla MANDATORY_ACTIVATION del Pasaporte Digital (HU-05).
 * POST /api/v1/xf/scanner/record-activation
 * @param {object} payload - { ticketFolioOrToken, stationCode, activationName, resultSummary, scoreValue, scanMethod }
 * @param {string} operatorJwt - Token JWT del operador
 * @returns {Promise<object>} XfRecordActivationResultDto
 */
export const RecordXfSponsorActivation = async (payload, operatorJwt) => {
    try {
        const body = {
            ticketFolioOrToken: String(
                payload?.ticketFolioOrToken || payload?.ticketFolio || payload?.token || ''
            ).trim(),
            stationCode: String(payload?.stationCode || 'ACT_CALIENTE').trim(),
            activationName: payload?.activationName ? String(payload.activationName).trim() : null,
            resultSummary: String(payload?.resultSummary || 'Participación Completada').trim(),
            scoreValue:
                payload?.scoreValue !== undefined &&
                payload?.scoreValue !== null &&
                payload?.scoreValue !== ''
                    ? Number(payload.scoreValue)
                    : null,
            scanMethod: String(payload?.scanMethod || 'MANUAL').trim().toUpperCase()
        };

        const response = await fetch(`${API_URL}v1/xf/scanner/record-activation`, {
            method: 'POST',
            headers: buildOperatorHeaders(operatorJwt),
            body: JSON.stringify(body)
        });

        if (response.status === 401 || response.status === 403) {
            return {
                success: false,
                isUnauthorized: true,
                status: response.status,
                message: 'Sesión de operador expirada o no autorizada.'
            };
        }

        const resData = await response.json().catch(() => ({}));

        if (!response.ok && !resData.status) {
            return {
                success: false,
                status: 'INVALID_TICKET',
                colorTheme: 'RED',
                audioCue: 'ERROR_BUZZER',
                title: 'ERROR EN ACTIVACIÓN',
                message: resData.message || 'No fue posible registrar la activación de marca.',
                ...resData
            };
        }

        return {
            success: Boolean(resData.success),
            status: resData.status || (resData.success ? 'ACTIVATION_RECORDED' : 'INVALID_TICKET'),
            colorTheme: resData.colorTheme || (resData.success ? 'GREEN' : 'RED'),
            audioCue: resData.audioCue || (resData.success ? 'SUCCESS_ARPEGGIO' : 'ERROR_BUZZER'),
            ...resData
        };
    } catch (error) {
        console.error('Error en RecordXfSponsorActivation DL:', error);
        return {
            success: false,
            status: 'NETWORK_ERROR',
            colorTheme: 'RED',
            audioCue: 'ERROR_BUZZER',
            title: 'ERROR DE CONEXIÓN',
            message: 'No fue posible conectar con el servidor para sellar la activación.'
        };
    }
};

/**
 * Canjea y registra la entrega física del souvenir oficial validando `#CANJE-XXXXXX`, folio o token QR,
 * bloqueando intentos de doble entrega (HU-05).
 * POST /api/v1/xf/scanner/redeem-reward
 * @param {object} payload - { ticketFolioOrCouponOrToken, stationCode, scanMethod }
 * @param {string} operatorJwt - Token JWT del operador
 * @returns {Promise<object>} XfRedeemRewardResultDto
 */
export const RedeemXfRewardCoupon = async (payload, operatorJwt) => {
    try {
        const body = {
            ticketFolioOrCouponOrToken: String(
                payload?.ticketFolioOrCouponOrToken ||
                    payload?.ticketFolioOrToken ||
                    payload?.couponCode ||
                    payload?.ticketFolio ||
                    ''
            ).trim(),
            stationCode: payload?.stationCode
                ? String(payload.stationCode).trim()
                : 'REWARD_BOOTH',
            scanMethod: String(payload?.scanMethod || 'MANUAL').trim().toUpperCase()
        };

        const response = await fetch(`${API_URL}v1/xf/scanner/redeem-reward`, {
            method: 'POST',
            headers: buildOperatorHeaders(operatorJwt),
            body: JSON.stringify(body)
        });

        if (response.status === 401 || response.status === 403) {
            return {
                success: false,
                isUnauthorized: true,
                status: response.status,
                message: 'Sesión de operador expirada o no autorizada.'
            };
        }

        const resData = await response.json().catch(() => ({}));

        if (!response.ok && !resData.status) {
            return {
                success: false,
                status: 'INVALID_TICKET',
                colorTheme: 'RED',
                audioCue: 'ERROR_BUZZER',
                title: 'ERROR EN CANJE DE PREMIO',
                message: resData.message || 'No fue posible validar o canjear el Cupón Dorado.',
                ...resData
            };
        }

        return {
            success: Boolean(resData.success),
            status: resData.status || (resData.success ? 'REWARD_REDEEMED' : 'INVALID_TICKET'),
            colorTheme: resData.colorTheme || (resData.success ? 'GREEN' : 'RED'),
            audioCue: resData.audioCue || (resData.success ? 'SUCCESS_ARPEGGIO' : 'ERROR_BUZZER'),
            ...resData
        };
    } catch (error) {
        console.error('Error en RedeemXfRewardCoupon DL:', error);
        return {
            success: false,
            status: 'NETWORK_ERROR',
            colorTheme: 'RED',
            audioCue: 'ERROR_BUZZER',
            title: 'ERROR DE CONEXIÓN',
            message: 'No fue posible conectar con el servidor de canje de premios.'
        };
    }
};

// ============================================================================
// MÉTODOS DEL CENTRO DE MANDO LIVE BOARD E INSPECTOR 360° (HU-06)
// ============================================================================

/**
 * Obtiene las métricas ejecutivas en tiempo real para el Centro de Mando Live Board (/demo/xolofest/board).
 * GET /api/v1/xf/admin/metrics
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export const GetXfLiveBoardMetrics = async () => {
    try {
        const response = await fetch(`${API_URL}v1/xf/admin/metrics`, {
            method: 'GET',
            headers: buildHeaders()
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message:
                    resData.message ||
                    'No fue posible obtener las métricas en vivo del Centro de Mando.',
                ...resData
            };
        }

        const metricsPayload = resData.data || resData.metrics || resData;
        return {
            success: true,
            data: metricsPayload,
            ...metricsPayload
        };
    } catch (error) {
        console.error('Error en GetXfLiveBoardMetrics DL:', error);
        return {
            success: false,
            message: 'Error de conexión al consultar las métricas del Centro de Mando.'
        };
    }
};

/**
 * Busca participantes registrados por folio, nombre, teléfono o correo (con PII enmascarado por defecto).
 * GET /api/v1/xf/admin/participants/search?q={query}
 * @param {string} query - Término de búsqueda
 * @returns {Promise<{ success: boolean, items: Array, message?: string }>}
 */
export const SearchXfParticipants = async (query = '') => {
    try {
        const cleanQuery = String(query || '').trim();
        const response = await fetch(
            `${API_URL}v1/xf/admin/participants/search?q=${encodeURIComponent(cleanQuery)}`,
            {
                method: 'GET',
                headers: buildHeaders()
            }
        );

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                items: [],
                status: response.status,
                message: resData.message || 'No fue posible realizar la búsqueda de participantes.'
            };
        }

        const items = Array.isArray(resData)
            ? resData
            : Array.isArray(resData.items)
            ? resData.items
            : Array.isArray(resData.participants)
            ? resData.participants
            : Array.isArray(resData.data)
            ? resData.data
            : [];

        return {
            success: true,
            items,
            data: resData
        };
    } catch (error) {
        console.error('Error en SearchXfParticipants DL:', error);
        return {
            success: false,
            items: [],
            message: 'Error de conexión al buscar participantes en el Inspector 360°.'
        };
    }
};

/**
 * Obtiene el expediente 360° completo de un participante por su folio, incluyendo PII enmascarado/crudo y su timeline.
 * GET /api/v1/xf/admin/participants/{ticketFolio}/360
 * @param {string} ticketFolio - Folio del boleto (ej. XOLO-0001)
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export const GetXfParticipant360 = async (ticketFolio) => {
    try {
        const cleanFolio = String(ticketFolio || '').trim().toUpperCase();
        const response = await fetch(
            `${API_URL}v1/xf/admin/participants/${encodeURIComponent(cleanFolio)}/360`,
            {
                method: 'GET',
                headers: buildHeaders()
            }
        );

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message:
                    resData.message ||
                    `No se encontró el expediente 360° para el folio ${cleanFolio}.`,
                ...resData
            };
        }

        const detail = resData.data || resData.participant || resData;
        return {
            success: true,
            data: detail,
            ...detail
        };
    } catch (error) {
        console.error('Error en GetXfParticipant360 DL:', error);
        return {
            success: false,
            message: 'Error de conexión al consultar el expediente 360° del aficionado.'
        };
    }
};

/**
 * Simula una ráfaga de tráfico en vivo (accesos en puertas, ventas POS y activaciones de marca) para demostración ejecutiva.
 * POST /api/v1/xf/admin/demo/simulate-traffic
 * @param {object} payload - { accessScansCount, purchasesCount, activationsCount }
 * @returns {Promise<object>} Resultado con métricas actualizadas (XfSimulateTrafficResultDto)
 */
export const SimulateXfTraffic = async (
    payload = { accessScansCount: 5, purchasesCount: 3, activationsCount: 2 }
) => {
    try {
        const body = {
            accessScansCount: Number(payload?.accessScansCount ?? 5),
            purchasesCount: Number(payload?.purchasesCount ?? 3),
            activationsCount: Number(payload?.activationsCount ?? 2),
            resetTraffic: Boolean(payload?.resetTraffic ?? false)
        };

        const response = await fetch(`${API_URL}v1/xf/admin/demo/simulate-traffic`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify(body)
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message:
                    resData.message ||
                    'No fue posible ejecutar la simulación de tráfico en el Centro de Mando.',
                ...resData
            };
        }

        return {
            success: resData.success !== undefined ? resData.success : true,
            ...resData
        };
    } catch (error) {
        console.error('Error en SimulateXfTraffic DL:', error);
        return {
            success: false,
            message: 'Error de conexión al simular tráfico en el Centro de Mando.'
        };
    }
};

/**
 * Reinicia los datos de demostración y restablece el estado inicial del evento Xolo Fest.
 * POST /api/v1/xf/admin/seed?forceResetTickets=true
 * @returns {Promise<object>}
 */
export const ResetXfDemoTraffic = async () => {
    try {
        const response = await fetch(`${API_URL}v1/xf/admin/seed?forceResetTickets=true`, {
            method: 'POST',
            headers: buildHeaders()
        });

        const resData = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: resData.message || 'No fue posible reiniciar el tráfico de demostración.'
            };
        }

        return {
            success: true,
            ...resData
        };
    } catch (error) {
        console.error('Error en ResetXfDemoTraffic DL:', error);
        return {
            success: false,
            message: 'Error de conexión al reiniciar el estado de demostración.'
        };
    }
};

/**
 * Borra y reinicia toda la información de Xolo Fest (participantes, bitácoras, compras POS, activaciones y aforos)
 * para comenzar una prueba limpia desde cero.
 * POST /api/v1/xf/admin/reset-all-data
 * @param {string} password - Contraseña de autorización
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export const ResetXfAllData = async (password) => {
    try {
        const response = await fetch(`${API_URL}v1/xf/admin/reset-all-data`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({ password })
        });

        const resData = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: resData.message || 'No fue posible reiniciar la información de Xolo Fest.'
            };
        }

        return {
            success: true,
            ...resData
        };
    } catch (error) {
        console.error('Error en ResetXfAllData DL:', error);
        return {
            success: false,
            message: 'Error de conexión al reiniciar los datos de Xolo Fest.'
        };
    }
};

// ============================================================================
// HU-07: EXPORTACIÓN EJECUTIVA EXCEL (.XLSX) Y RESILIENCIA PERIMETRAL OFFLINE
// ============================================================================

export const XF_OFFLINE_QUEUE_STORAGE_KEY = 'xf_scanner_offline_queue';

/**
 * Descarga el Reporte Ejecutivo Multi-Hoja en formato nativo Excel (.xlsx).
 * GET /api/v1/xf/admin/reports/export-excel?maskPii={true|false}
 * @param {boolean} maskPii - true = enmascara datos personales (PII Protegido), false = reporte completo de auditoría
 * @returns {Promise<{ success: boolean, fileName?: string, message?: string }>}
 */
export const ExportXfExcelReport = async (maskPii = false) => {
    try {
        const query = maskPii ? 'true' : 'false';
        const response = await fetch(`${API_URL}v1/xf/admin/reports/export-excel?maskPii=${query}`, {
            method: 'GET',
            headers: {
                Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
        });

        if (!response.ok) {
            return {
                success: false,
                status: response.status,
                message: 'No fue posible generar el archivo Excel (.xlsx) desde el servidor.'
            };
        }

        const blob = await response.blob();
        const dateStamp = new Date().toISOString().slice(0, 10);
        const suffix = maskPii ? 'PII_Protegido' : 'Auditoria';
        const fileName = `Reporte_Ejecutivo_XoloFest_2026_${suffix}_${dateStamp}.xlsx`;

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        return {
            success: true,
            fileName
        };
    } catch (error) {
        console.error('Error en ExportXfExcelReport DL:', error);
        return {
            success: false,
            message: 'Error de conexión al descargar el reporte ejecutivo Excel (.xlsx).'
        };
    }
};

/**
 * Obtiene la cola local de escaneos almacenados en modo offline (localStorage).
 * @returns {Array<object>}
 */
export const GetOfflineScanQueue = () => {
    try {
        const raw = window.localStorage.getItem(XF_OFFLINE_QUEUE_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        console.error('Error leyendo cola offline de escaneos:', error);
        return [];
    }
};

/**
 * Agrega un escaneo de puerta a la cola local offline con clientScanId único para sincronización idempotente.
 * @param {object} scanItem - { clientScanId, ticketFolioOrToken, stationCode, scanMethod, queuedAtUtc }
 * @returns {Array<object>} Cola actualizada
 */
export const EnqueueOfflineScan = (scanItem) => {
    try {
        const currentQueue = GetOfflineScanQueue();
        const clientScanId =
            scanItem?.clientScanId ||
            `offline_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        const entry = {
            clientScanId,
            ticketFolioOrToken: String(scanItem?.ticketFolioOrToken || '').trim(),
            stationCode: String(scanItem?.stationCode || 'P1_ACCESO').trim(),
            scanMethod: String(scanItem?.scanMethod || 'OFFLINE_QUEUE').trim().toUpperCase(),
            queuedAtUtc: scanItem?.queuedAtUtc || new Date().toISOString()
        };
        const updatedQueue = [...currentQueue, entry];
        window.localStorage.setItem(XF_OFFLINE_QUEUE_STORAGE_KEY, JSON.stringify(updatedQueue));
        return updatedQueue;
    } catch (error) {
        console.error('Error guardando escaneo en cola offline:', error);
        return GetOfflineScanQueue();
    }
};

/**
 * Limpia por completo la cola local de escaneos offline.
 */
export const ClearOfflineScanQueue = () => {
    try {
        window.localStorage.removeItem(XF_OFFLINE_QUEUE_STORAGE_KEY);
    } catch (error) {
        console.error('Error limpiando cola offline:', error);
    }
};

/**
 * Procesa secuencialmente la cola local de escaneos offline enviando cada registro con su clientScanId
 * a POST /api/v1/xf/scanner/check-access para garantizar idempotencia.
 * @param {string} operatorJwt - Token JWT del operador activo
 * @returns {Promise<{ success: boolean, syncedCount: number, failedCount: number, remainingCount: number, results: Array<object> }>}
 */
export const ProcessOfflineScanQueue = async (operatorJwt) => {
    const queue = GetOfflineScanQueue();
    if (!queue.length) {
        return {
            success: true,
            syncedCount: 0,
            failedCount: 0,
            remainingCount: 0,
            results: []
        };
    }

    const remaining = [];
    const results = [];
    let syncedCount = 0;
    let failedCount = 0;

    for (let i = 0; i < queue.length; i += 1) {
        const item = queue[i];
        // eslint-disable-next-line no-await-in-loop
        const response = await CheckXfAccessScan(
            {
                ticketFolioOrToken: item.ticketFolioOrToken,
                stationCode: item.stationCode,
                scanMethod: item.scanMethod || 'OFFLINE_SYNC',
                clientScanId: item.clientScanId
            },
            operatorJwt
        );

        if (response?.status === 'NETWORK_ERROR') {
            remaining.push(item, ...queue.slice(i + 1));
            failedCount += queue.length - i;
            break;
        }

        syncedCount += 1;
        results.push({
            clientScanId: item.clientScanId,
            ticketFolioOrToken: item.ticketFolioOrToken,
            stationCode: item.stationCode,
            response
        });
    }

    try {
        if (remaining.length > 0) {
            window.localStorage.setItem(XF_OFFLINE_QUEUE_STORAGE_KEY, JSON.stringify(remaining));
        } else {
            window.localStorage.removeItem(XF_OFFLINE_QUEUE_STORAGE_KEY);
        }
    } catch (error) {
        console.error('Error actualizando cola offline tras sincronización:', error);
    }

    return {
        success: remaining.length === 0,
        syncedCount,
        failedCount,
        remainingCount: remaining.length,
        results
    };
};




